import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getWeatherPreferences,
  updateWeatherPreferences,
} from "../services/api";
import axios from "axios";
import { RootState } from "./store";
import { WeatherPreferences, Location } from "../types";

export const defaultWeatherPreferences: WeatherPreferences = {
  cloudCoverThreshold: 30,
  minTemperature: 40,
  maxWindSpeed: 15,
  precipitationThreshold: 20,
  preferredTime: "night",
  preferredMoonPhase: "any",
  notificationEnabled: true,
  notificationLeadTime: 60,
  units: "imperial",
  locations: [], // Empty array as default
  max_acceptable_clouds: 60,
  min_acceptable_visibility: 0.5,
  auto_refresh: true,
  refresh_interval: 15 * 60 * 1000, // 15 minutes
};

interface WeatherPreferencesState {
  data: WeatherPreferences | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  saveError: string | null;
  lastFetched: string | null;
  lastSaved: string | null;
  isDirty: boolean;
  dirtyFields: string[];
}

const initialState: WeatherPreferencesState = {
  data: null,
  loading: false,
  saving: false,
  error: null,
  saveError: null,
  lastFetched: null,
  lastSaved: null,
  isDirty: false,
  dirtyFields: [],
};

export const fetchWeatherPreferences = createAsyncThunk(
  "weatherPreferences/fetch",
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState() as RootState;
      const token = state.auth.token;

      if (!token) {
        return rejectWithValue("Authentication required");
      }

      const data = await getWeatherPreferences();
      return data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(
          error.response.data.message || "Failed to fetch weather preferences",
        );
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Unknown error occurred");
    }
  },
);

export const updateWeatherPreferencesThunk = createAsyncThunk(
  "weatherPreferences/update",
  async (
    preferences: Partial<WeatherPreferences>,
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as RootState;
      const token = state.auth.token;
      const currentPrefs = state.weatherPreferences.data;

      if (!token) {
        return rejectWithValue("Authentication required");
      }

      if (!currentPrefs) {
        return rejectWithValue("No existing preferences to update");
      }

      const updatedPreferences: WeatherPreferences = {
        ...currentPrefs,
        ...preferences,
      };

      if (preferences.units && preferences.units !== currentPrefs.units) {
        if (
          preferences.units === "metric" &&
          currentPrefs.units === "imperial"
        ) {
          // Only perform conversion if values are defined
          if (updatedPreferences.minTemperature !== undefined) {
            updatedPreferences.minTemperature = Math.round(
              ((updatedPreferences.minTemperature - 32) * 5) / 9,
            );
          }
          if (updatedPreferences.maxWindSpeed !== undefined) {
            updatedPreferences.maxWindSpeed = Math.round(
              updatedPreferences.maxWindSpeed * 1.60934,
            );
          }
        } else if (
          preferences.units === "imperial" &&
          currentPrefs.units === "metric"
        ) {
          // Only perform conversion if values are defined
          if (updatedPreferences.minTemperature !== undefined) {
            updatedPreferences.minTemperature = Math.round(
              (updatedPreferences.minTemperature * 9) / 5 + 32,
            );
          }
          if (updatedPreferences.maxWindSpeed !== undefined) {
            updatedPreferences.maxWindSpeed = Math.round(
              updatedPreferences.maxWindSpeed / 1.60934,
            );
          }
        }
      }

      await updateWeatherPreferences(updatedPreferences);
      return updatedPreferences;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(
          error.response.data.message || "Failed to update weather preferences",
        );
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Unknown error occurred");
    }
  },
);

export const addLocation = createAsyncThunk(
  "weatherPreferences/addLocation",
  async (
    location: {
      name: string;
      latitude: number;
      longitude: number;
      isPrimary?: boolean;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    try {
      const state = getState() as RootState;
      const currentPrefs = state.weatherPreferences.data;

      if (!currentPrefs) {
        return rejectWithValue("No existing preferences");
      }

      const locationId = `loc_${Date.now()}`;
      const newLocation: Location = {
        id: locationId,
        name: location.name,
        latitude: location.latitude,
        longitude: location.longitude,
        isPrimary: location.isPrimary || ((currentPrefs.locations?.length || 0) === 0),
      };

      // Ensure we have an array to work with
      let updatedLocations = [...(currentPrefs.locations || [])];
      if (newLocation.isPrimary) {
        updatedLocations = updatedLocations.map((loc) => ({
          ...loc,
          isPrimary: false,
        }));
      }
      updatedLocations.push(newLocation);

      const result = await dispatch(
        updateWeatherPreferencesThunk({
          locations: updatedLocations,
        }),
      ).unwrap();

      return {
        newLocation,
        updatedPreferences: result,
      };
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to add location");
    }
  },
);

export const removeLocation = createAsyncThunk(
  "weatherPreferences/removeLocation",
  async (locationId: string, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState() as RootState;
      const currentPrefs = state.weatherPreferences.data;

      if (!currentPrefs) {
        return rejectWithValue("No existing preferences");
      }

      // Ensure locations exists
      if (!currentPrefs.locations || currentPrefs.locations.length === 0) {
        return rejectWithValue("No locations found");
      }
      
      const locationToRemove = currentPrefs.locations.find(
        (loc) => loc.id === locationId,
      );

      if (!locationToRemove) {
        return rejectWithValue("Location not found");
      }

      let updatedLocations = currentPrefs.locations.filter(
        (loc) => loc.id !== locationId,
      );

      if (locationToRemove.isPrimary && updatedLocations.length > 0) {
        updatedLocations[0].isPrimary = true;
      }

      const result = await dispatch(
        updateWeatherPreferencesThunk({
          locations: updatedLocations,
        }),
      ).unwrap();

      return {
        removedLocationId: locationId,
        updatedPreferences: result,
      };
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to remove location");
    }
  },
);

export const setPrimaryLocation = createAsyncThunk(
  "weatherPreferences/setPrimaryLocation",
  async (locationId: string, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState() as RootState;
      const currentPrefs = state.weatherPreferences.data;

      if (!currentPrefs) {
        return rejectWithValue("No existing preferences");
      }

      // Ensure locations exists
      if (!currentPrefs.locations || !currentPrefs.locations.length) {
        return rejectWithValue("No locations found");
      }
      
      if (!currentPrefs.locations || !currentPrefs.locations.some((loc) => loc.id === locationId)) {
        return rejectWithValue("Location not found");
      }

      const updatedLocations = currentPrefs.locations.map((loc) => ({
        ...loc,
        isPrimary: loc.id === locationId,
      }));

      const result = await dispatch(
        updateWeatherPreferencesThunk({
          locations: updatedLocations,
        }),
      ).unwrap();

      return {
        primaryLocationId: locationId,
        updatedPreferences: result,
      };
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to set primary location");
    }
  },
);

const weatherPreferencesSlice = createSlice({
  name: "weatherPreferences",
  initialState,
  reducers: {
    updateLocalPreferences(
      state,
      action: PayloadAction<Partial<WeatherPreferences>>,
    ) {
      if (state.data) {
        const dirtyFields = new Set(state.dirtyFields);
        Object.keys(action.payload).forEach((key) => {
          dirtyFields.add(key);
        });

        state.data = {
          ...state.data,
          ...action.payload,
        };

        state.isDirty = true;
        state.dirtyFields = Array.from(dirtyFields);
      }
    },

    resetLocalChanges(state) {
      state.isDirty = false;
      state.dirtyFields = [];
    },

    clearErrors(state) {
      state.error = null;
      state.saveError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWeatherPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWeatherPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.data = {
          ...defaultWeatherPreferences,
          ...action.payload,
        };
        state.lastFetched = new Date().toISOString();
        state.isDirty = false;
        state.dirtyFields = [];
      })
      .addCase(fetchWeatherPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        if (!state.data) {
          state.data = { ...defaultWeatherPreferences };
          state.isDirty = true;
        }
      })
      .addCase(updateWeatherPreferencesThunk.pending, (state) => {
        state.saving = true;
        state.saveError = null;
      })
      .addCase(updateWeatherPreferencesThunk.fulfilled, (state, action) => {
        state.saving = false;
        state.data = action.payload;
        state.lastSaved = new Date().toISOString();
        state.isDirty = false;
        state.dirtyFields = [];
      })
      .addCase(updateWeatherPreferencesThunk.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload as string;
      });
  },
});

export const { updateLocalPreferences, resetLocalChanges, clearErrors } =
  weatherPreferencesSlice.actions;

export const selectWeatherPreferences = (state: RootState) =>
  state.weatherPreferences.data;
export const selectWeatherPreferencesLoading = (state: RootState) =>
  state.weatherPreferences.loading;
export const selectWeatherPreferencesSaving = (state: RootState) =>
  state.weatherPreferences.saving;
export const selectWeatherPreferencesError = (state: RootState) =>
  state.weatherPreferences.error;
export const selectWeatherPreferencesSaveError = (state: RootState) =>
  state.weatherPreferences.saveError;
export const selectIsWeatherPreferencesDirty = (state: RootState) =>
  state.weatherPreferences.isDirty;
export const selectWeatherPreferencesLocations = (state: RootState) =>
  state.weatherPreferences.data?.locations || [];
export const selectPrimaryLocation = (state: RootState) =>
  state.weatherPreferences.data?.locations?.find((loc) => loc.isPrimary);
export const selectPreferredUnits = (state: RootState) =>
  state.weatherPreferences.data?.units || "imperial";

export default weatherPreferencesSlice.reducer;
